// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { BULK_OPS_SELECTED_ISSUE_LIMIT } from '@atlassian/jira-bulk-operations-common/src/common/constants.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { SelectedIssuesState, IssueTableSelectedIssuesContainerProps } from './types.tsx';

export const initialState: SelectedIssuesState = {
	isSelectingAll: false,
	selectedIssueIds: new Set([]),
	excludedIssueIds: new Set([]),
	lastSelectedRowIndex: null,
	totalIssueSearchResultCount: null,
};

export const actions = {
	selectAllIssues:
		(data: {
			childIds?: string[];
			visibleIds: string[];
			count?: number | null;
		}): Action<SelectedIssuesState, IssueTableSelectedIssuesContainerProps> =>
		({ dispatch, setState }, { onIssueSelected, shouldForceManualSelectAll }) => {
			if (shouldForceManualSelectAll) {
				const { visibleIds } = data;
				// when grouping is enabled or the first page is not loaded we should disable "true" select all functionality
				// and fallback to selecting all the loaded issue ids
				dispatch(actions.insertSelectedIssueIds(visibleIds));
			} else {
				const { childIds, count } = data;
				// otherwise conditions are met for true select all
				onIssueSelected?.([]);
				setState({
					isSelectingAll: true,
					selectedIssueIds: new Set(childIds ?? []),
					excludedIssueIds: new Set([]),
					totalIssueSearchResultCount: count,
				});
			}
		},
	deselectAllIssues:
		(): Action<SelectedIssuesState, IssueTableSelectedIssuesContainerProps> =>
		({ setState }) => {
			setState({
				isSelectingAll: false,
				selectedIssueIds: new Set([]),
				excludedIssueIds: new Set([]),
				lastSelectedRowIndex: null,
			});
		},
	checkIssueRowSelectAll:
		(
			selectedIssueIds: string[],
			isChecked: boolean,
			isShiftPressed: boolean,
			flatList: string[],
			currentCheckedIndex: number,
			depth?: number,
		): Action<SelectedIssuesState, IssueTableSelectedIssuesContainerProps> =>
		({ dispatch, getState, setState }) => {
			const currentState = getState();

			if (isShiftPressed && currentState.lastSelectedRowIndex !== null) {
				const beyondLimitIssueIds = selectedIssueIds.filter((id) => {
					const idx = flatList.findIndex((flatId) => flatId === id);
					return idx > BULK_OPS_SELECTED_ISSUE_LIMIT - 1;
				});
				const withinLimitIssueIds = selectedIssueIds.filter((id) => {
					const idx = flatList.findIndex((flatId) => flatId === id);
					return idx < BULK_OPS_SELECTED_ISSUE_LIMIT;
				});

				if (isChecked) {
					const updatedExcludedIssueIds = new Set(currentState.excludedIssueIds);
					withinLimitIssueIds.forEach((id) => updatedExcludedIssueIds.delete(id));

					setState({
						selectedIssueIds: new Set([...currentState.selectedIssueIds, ...beyondLimitIssueIds]),
						excludedIssueIds: updatedExcludedIssueIds,
					});
				} else {
					const updatedSelectedIssueIds = new Set(currentState.selectedIssueIds);
					beyondLimitIssueIds.forEach((id) => updatedSelectedIssueIds.delete(id));
					const updatedExcludedIssueIds = new Set([
						...currentState.excludedIssueIds,
						...withinLimitIssueIds,
					]);

					setState({
						selectedIssueIds: updatedSelectedIssueIds,
						excludedIssueIds: updatedExcludedIssueIds,
					});

					// if all issues have been excluded, we are not selecting all anymore
					if (updatedExcludedIssueIds.size === currentState.totalIssueSearchResultCount) {
						dispatch(actions.reset());
					}
				}

				return;
			}

			if (isChecked) {
				dispatch(actions.addToSelectAllIssues(selectedIssueIds, currentCheckedIndex, depth));
			} else {
				dispatch(actions.excludeFromSelectAllIssues(selectedIssueIds, currentCheckedIndex, depth));
			}
		},
	excludeFromSelectAllIssues:
		(
			issueIds: string[],
			rowIndex: number,
			depth = 0,
		): Action<SelectedIssuesState, IssueTableSelectedIssuesContainerProps> =>
		({ setState, getState, dispatch }) => {
			const currentState = getState();

			if (rowIndex <= BULK_OPS_SELECTED_ISSUE_LIMIT - 1 && depth === 0) {
				const updatedExcludedIssueIds = new Set([...currentState.excludedIssueIds, ...issueIds]);
				setState({
					excludedIssueIds: updatedExcludedIssueIds,
				});

				// if all issues have been excluded, we are not selecting all anymore
				if (updatedExcludedIssueIds.size === currentState.totalIssueSearchResultCount) {
					dispatch(actions.reset());
				}
			} else {
				const updatedSelectedIssueIds = new Set(currentState.selectedIssueIds);
				issueIds.forEach((issueId) => updatedSelectedIssueIds.delete(issueId));

				setState({
					selectedIssueIds: updatedSelectedIssueIds,
				});
			}
		},
	addToSelectAllIssues:
		(
			issueIds: string[],
			rowIndex: number,
			depth = 0,
		): Action<SelectedIssuesState, IssueTableSelectedIssuesContainerProps> =>
		({ setState, getState }) => {
			const currentState = getState();

			if (rowIndex > BULK_OPS_SELECTED_ISSUE_LIMIT - 1 || depth > 0) {
				setState({
					selectedIssueIds: new Set([...currentState.selectedIssueIds, ...issueIds]),
				});
			} else {
				const updatedExcludedIssueIds = new Set(currentState.excludedIssueIds);
				issueIds.forEach((issueId) => updatedExcludedIssueIds.delete(issueId));

				setState({
					excludedIssueIds: updatedExcludedIssueIds,
				});
			}
		},
	insertSelectedIssueIds:
		(
			selectedIssueIds: string[],
		): Action<SelectedIssuesState, IssueTableSelectedIssuesContainerProps> =>
		({ setState, getState }, { onIssueSelected }) => {
			const currentState = getState();
			onIssueSelected?.(selectedIssueIds);

			setState({
				selectedIssueIds: new Set([...currentState.selectedIssueIds, ...selectedIssueIds]),
			});
		},
	removeSelectedIssueIds:
		(
			selectedIssueIds: string[],
		): Action<SelectedIssuesState, IssueTableSelectedIssuesContainerProps> =>
		({ setState, getState }) => {
			const currentState = getState();
			const updatedSelectedIssueIds = new Set(currentState.selectedIssueIds);
			selectedIssueIds.forEach((selectedIssueId) => {
				updatedSelectedIssueIds.delete(selectedIssueId);
			});

			setState({
				selectedIssueIds: updatedSelectedIssueIds,
			});
		},
	insertSelectedIssueId:
		(
			selectedIssueId: string,
		): Action<SelectedIssuesState, IssueTableSelectedIssuesContainerProps> =>
		({ setState, getState }, { onIssueSelected }) => {
			const currentState = getState();
			const updatedSelectedIssueIds = new Set(currentState.selectedIssueIds);
			updatedSelectedIssueIds.add(selectedIssueId);

			onIssueSelected?.([selectedIssueId]);

			setState({
				selectedIssueIds: updatedSelectedIssueIds,
			});
		},
	removeSelectedIssueId:
		(selectedIssueId: string): Action<SelectedIssuesState> =>
		({ setState, getState }) => {
			const currentState = getState();
			const updatedSelectedIssueIds = new Set(currentState.selectedIssueIds);
			updatedSelectedIssueIds.delete(selectedIssueId);

			setState({
				selectedIssueIds: updatedSelectedIssueIds,
			});
		},
	selectedOnlyIssueId:
		(
			selectedIssueId: string,
		): Action<SelectedIssuesState, IssueTableSelectedIssuesContainerProps> =>
		({ setState }, { onIssueSelected }) => {
			onIssueSelected?.([selectedIssueId]);
			setState({ selectedIssueIds: new Set([selectedIssueId]) });
		},
	updateLastSelectedRowIndex:
		(lastSelectedRowIndex: number): Action<SelectedIssuesState> =>
		({ setState }) => {
			setState({ lastSelectedRowIndex });
		},
	reset:
		(): Action<SelectedIssuesState> =>
		({ setState }) => {
			setState(initialState);
		},
	checkIssueRow:
		(
			issueId: string,
			isChecked: boolean,
			isShiftPressed: boolean,
			flatList: string[],
			depth?: number,
		): Action<SelectedIssuesState, IssueTableSelectedIssuesContainerProps> =>
		({ dispatch, getState }) => {
			const selectedIssueIds = [issueId];
			const currentState = getState();
			const currentCheckedIndex = flatList.findIndex((id) => id === issueId);

			if (isShiftPressed && currentState.lastSelectedRowIndex !== null) {
				const start = Math.min(currentState.lastSelectedRowIndex, currentCheckedIndex);
				const end = Math.max(currentState.lastSelectedRowIndex, currentCheckedIndex);
				selectedIssueIds.push(...flatList.slice(start, end + 1).filter((id) => id !== issueId));
			}

			dispatch(actions.updateLastSelectedRowIndex(currentCheckedIndex));

			if (
				currentState.isSelectingAll &&
				expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)
			) {
				dispatch(
					actions.checkIssueRowSelectAll(
						selectedIssueIds,
						isChecked,
						isShiftPressed,
						flatList,
						currentCheckedIndex,
						depth,
					),
				);
			} else if (isChecked) {
				dispatch(actions.insertSelectedIssueIds(selectedIssueIds));
			} else {
				dispatch(actions.removeSelectedIssueIds(selectedIssueIds));
			}
		},
} as const;
export type Actions = typeof actions;
